import type { ReactNode } from "react";
import styles from "./Container.module.css";

/*
  An HTML tag name, rather than React's `ElementType`.

  `ElementType` resolves through `JSX.IntrinsicElements`, and react-three-fiber
  adds every three.js object to that interface globally for the hero graphic's
  sake — `<mesh>`, `<group>`, and a few hundred others, none of which take a
  `className`. Naming the tags we mean keeps them out.
*/
type HtmlTag = keyof HTMLElementTagNameMap;

/*
  The centred content column. The export called this `standard-outer-div` and
  used it on every page, then overrode its vertical padding through a second
  class about a dozen times — so the padding is a prop here instead.
*/
export function Container({
  children,
  as: Tag = "div",
  padding = "section",
  className,
  id,
}: {
  children: ReactNode;
  as?: HtmlTag;
  /* "section" is the default 80px band; "none" is for headers and heroes that
     bring their own vertical rhythm. */
  padding?: "section" | "tight" | "none";
  className?: string;
  id?: string;
}) {
  /*
    "none" intentionally emits NO padding class. The base `.container` sets no
    padding-block, so the result is the same 0 — but crucially there is then no
    `.none { padding-block: 0 }` rule sitting on the same element as a hero's
    `.heroInner { padding-top: 140px }`. Those two are equal specificity, so the
    winner is decided by CSS-module injection order, which differs between a hard
    load and a client-side navigation. When `.none` landed last, it zeroed the
    hero's top padding and the fixed header overlapped the title. Dropping the
    class removes the collision entirely, so order can never matter.
  */
  const paddingClass = padding === "none" ? undefined : styles[padding];
  return (
    <Tag id={id} className={[styles.container, paddingClass, className].filter(Boolean).join(" ")}>
      {children}
    </Tag>
  );
}
